import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, MapPinned, Plus, Search, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { GlassCard } from '@/components/custom/GlassCard';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  addTicketAddress,
  removeTicketAddress,
  fetchResolvePreview,
  resolveTicket,
  searchDmpStreet,
} from '@/hooks/useAddressValidation';
import type {
  AddressValidationPanelsResponse,
  DmpStreetSearchItem,
  TicketAddressSummary,
  TicketResolvePreview,
  UniticketWriteResult,
} from '@uniticket/shared';

// ── N45 (plan validé le 20-10-2026) — plusieurs adresses dans un ticket ──────────────────────────────────────────────
// La barre des adresses (un onglet par adresse, « Ajouter une adresse ») et la carte « Résoudre le ticket ». Les règles
// vivent côté serveur ; ici, on montre et on dit pourquoi un geste est refusé.

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Un refus du serveur, dit en clair d'après son code. */
export function ticketAddressErrorText(err: unknown, t: TFn): string {
  const body = err instanceof ApiError ? err.body : undefined;
  const code = typeof body?.code === 'string' ? body.code : undefined;
  const params = (body?.params ?? {}) as Record<string, unknown>;
  switch (code) {
    case 'IN_OTHER_OPEN_TICKET': return t('tickets:validation.multi.errInOtherTicket', { ticket: String(params.ticketNumber ?? '—') });
    case 'ALREADY_IN_TICKET': return t('tickets:validation.multi.errAlready');
    case 'ADDRESS_NOT_FOUND': return t('tickets:validation.multi.errNotFound');
    case 'HANDOFF_PENDING': return t('tickets:validation.multi.errHandoff');
    case 'NO_PRIMARY_ADDRESS': return t('tickets:validation.multi.errNoPrimary');
    case 'DMP_WRITES': return t('tickets:validation.multi.errDmpWrites');
    case 'PRIMARY_ADDRESS': return t('tickets:validation.multi.errPrimary');
    case 'TICKET_LEVEL_HANDOFF': return t('tickets:validation.multi.errTicketLevel');
    case 'NOT_A_TICKET_ADDRESS': return t('tickets:validation.multi.errNotInTicket');
    case 'NOT_READY': return t('tickets:validation.multi.errNotReady');
    default: return (err as Error)?.message || t('tickets:validation.multi.errGeneric');
  }
}

/** Validée ou rejetée : l'adresse est traitée. */
export const isFinalAddressStatus = (s: string) => s === 'VALIDATED' || s === 'REJECTED';
/** Le nom d'une adresse du ticket — son onglet, ses messages. */
export const ticketAddressLabel = (a: TicketAddressSummary) => a.label ?? `JVID ${a.jvid}`;
const isFinal = isFinalAddressStatus;
const tabLabel = ticketAddressLabel;

export function TicketAddressBar({
  ticketId, addresses, activeJvid, multi, disabled, busy, onSelect, onAdded, onRemoved,
}: {
  ticketId: string;
  addresses: TicketAddressSummary[];
  activeJvid: number | null;
  multi: boolean;
  /** Un collègue tient le ticket : on lit, on n'agit pas (le serveur répond 423 de toute façon). */
  disabled: boolean;
  /** Un onglet est en cours de lecture. */
  busy?: boolean;
  onSelect: (jvid: number) => void;
  onAdded: (jvid: number, panels: AddressValidationPanelsResponse) => void;
  /** La réponse est la fenêtre sur l'adresse principale. */
  onRemoved: (jvid: number, panels: AddressValidationPanelsResponse) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [adding, setAdding] = useState(false);
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [jvidInput, setJvidInput] = useState('');
  const [results, setResults] = useState<DmpStreetSearchItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busyJvid, setBusyJvid] = useState<number | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [toRemove, setToRemove] = useState<TicketAddressSummary | null>(null);
  const [removing, setRemoving] = useState(false);
  const inTicket = new Set(addresses.map((a) => a.jvid));
  const done = addresses.filter((a) => isFinal(a.status)).length;

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    if (street.trim().length < 3) return;
    setSearching(true);
    setAddError(null);
    try {
      const res = await searchDmpStreet(street, number);
      setResults(res.data);
    } catch (err) {
      setResults([]);
      setAddError((err as Error)?.message || t('tickets:validation.multi.searchError'));
    } finally {
      setSearching(false);
    }
  }

  async function add(jvid: number) {
    setBusyJvid(jvid);
    setAddError(null);
    try {
      const res = await addTicketAddress(ticketId, jvid);
      const label = res.data.addresses?.find((a) => a.jvid === jvid)?.label ?? `JVID ${jvid}`;
      toast.success(t('tickets:validation.multi.added', { label }));
      setAdding(false);
      setResults(null);
      setStreet('');
      setNumber('');
      setJvidInput('');
      onAdded(jvid, res.data);
    } catch (err) {
      // Persistant, là où le geste a été fait : un refus qui s'efface avec le toast se relit mal.
      setAddError(ticketAddressErrorText(err, t));
    } finally {
      setBusyJvid(null);
    }
  }

  async function remove() {
    if (!toRemove) return;
    setRemoving(true);
    try {
      const res = await removeTicketAddress(ticketId, toRemove.jvid);
      toast.success(t('tickets:validation.multi.removed', { label: tabLabel(toRemove) }));
      onRemoved(toRemove.jvid, res.data);
      setToRemove(null);
    } catch (err) {
      toast.error(ticketAddressErrorText(err, t), { duration: 10000 });
      setToRemove(null);
    } finally {
      setRemoving(false);
    }
  }

  const addButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 text-xs"
      onClick={() => { setAdding((v) => !v); setAddError(null); }}
      disabled={disabled}
      aria-expanded={adding}
    >
      <Plus className="mr-1 h-3.5 w-3.5" />
      {t('tickets:validation.multi.addBtn')}
    </Button>
  );

  return (
    <GlassCard className={cn('p-4', !multi && !adding && 'p-2')}>
      {multi ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MapPinned className="h-4 w-4 text-brand-rose" />
            <h2 className="text-sm font-bold text-slate-800">{t('tickets:validation.multi.barTitle')}</h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
              {t('tickets:validation.multi.progress', { done, count: addresses.length })}
            </span>
            {busy && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" aria-label={t('tickets:validation.multi.tabLoading')} />
            )}
          </div>
          {addButton}
        </div>
      ) : (
        <div className="flex items-center justify-end">{addButton}</div>
      )}

      {multi && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('tickets:validation.multi.barTitle')}>
          {addresses.map((a) => {
            const active = a.jvid === activeJvid;
            return (
              <div
                key={a.jvid}
                className={cn(
                  'flex items-stretch overflow-hidden rounded-xl border transition-colors',
                  active ? 'border-brand-rose bg-brand-rose/5 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300',
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelect(a.jvid)}
                  aria-pressed={active}
                  className="min-h-[44px] px-3 py-1.5 text-left"
                >
                  <span className="block text-sm font-semibold text-slate-800">{tabLabel(a)}</span>
                  <span className="flex items-center gap-1.5 text-[11px]">
                    <span
                      className={cn(
                        'font-semibold',
                        a.status === 'VALIDATED' ? 'text-emerald-700'
                          : a.status === 'REJECTED' ? 'text-rose-700' : 'text-amber-700',
                      )}
                    >
                      {t(a.status === 'VALIDATED' ? 'tickets:validation.multi.tabValidated'
                        : a.status === 'REJECTED' ? 'tickets:validation.multi.tabRejected' : 'tickets:validation.multi.tabTodo')}
                    </span>
                    <span className="text-slate-500">· JVID {a.jvid}</span>
                    {a.primary && <span className="text-slate-500">· {t('tickets:validation.multi.primary')}</span>}
                    {a.ownedByOtherTicket && (
                      <span className="font-semibold text-amber-700">· {t('tickets:validation.multi.elsewhere')}</span>
                    )}
                  </span>
                </button>
                {!a.primary && (
                  <button
                    type="button"
                    onClick={() => setToRemove(a)}
                    disabled={disabled}
                    aria-label={t('tickets:validation.multi.removeLabel', { label: tabLabel(a) })}
                    title={t('tickets:validation.multi.removeLabel', { label: tabLabel(a) })}
                    className="flex min-w-[36px] items-center justify-center border-l border-slate-200 px-2 text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold text-slate-700">{t('tickets:validation.multi.addTitle')}</p>
          <p className="mb-2 text-[11px] text-slate-500">{t('tickets:validation.multi.addHint')}</p>
          <form onSubmit={search} className="flex flex-wrap items-center gap-2">
            <Input
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder={t('tickets:validation.multi.streetPh')}
              aria-label={t('tickets:validation.multi.streetPh')}
              className="h-9 min-w-[14rem] flex-1"
            />
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder={t('tickets:validation.multi.numberPh')}
              aria-label={t('tickets:validation.multi.numberPh')}
              className="h-9 w-24"
            />
            <Button type="submit" variant="outline" size="sm" className="h-9" disabled={searching || street.trim().length < 3}>
              {searching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1 h-3.5 w-3.5" />}
              {t('tickets:validation.multi.searchBtn')}
            </Button>
          </form>
          {results && (
            <div className="mt-2 space-y-1.5">
              {results.length === 0 && <p className="text-xs text-slate-500">{t('tickets:validation.multi.noResult')}</p>}
              {results.map((r) => {
                const already = inTicket.has(r.jvid);
                const num = r.houseNumberExtension || r.houseNumber || '';
                return (
                  <div key={r.jvid} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="min-w-0 text-xs text-slate-700">
                      <span className="font-semibold">{r.jvid}</span>
                      {' · '}{[r.streetName, num].filter(Boolean).join(' ')}{r.busNumber ? ` bte ${r.busNumber}` : ''}
                      {' · '}{[r.postalCode, r.city || r.municipality].filter(Boolean).join(' ')}
                      {r.status ? <span className="text-slate-500"> · {r.status}</span> : null}
                    </span>
                    {already ? (
                      <span className="text-[11px] font-semibold text-emerald-700">{t('tickets:validation.multi.alreadyIn')}</span>
                    ) : (
                      <Button type="button" size="sm" className="h-8 bg-brand-gradient text-white hover:opacity-90"
                        onClick={() => add(r.jvid)} disabled={disabled || busyJvid != null}>
                        {busyJvid === r.jvid ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
                        {t('tickets:validation.multi.addThisBtn')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-600">{t('tickets:validation.multi.jvidLabel')}</span>
            <Input
              value={jvidInput}
              onChange={(e) => setJvidInput(e.target.value.replace(/\D/g, ''))}
              placeholder="JVID"
              aria-label="JVID"
              className="h-9 w-36"
            />
            <Button type="button" variant="outline" size="sm" className="h-9"
              onClick={() => add(Number(jvidInput))} disabled={disabled || !jvidInput || busyJvid != null}>
              {t('tickets:validation.multi.addJvidBtn')}
            </Button>
          </div>
          {addError && (
            <p role="alert" className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{addError}</p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={toRemove != null}
        tone="rose"
        icon={<X className="h-5 w-5" />}
        title={t('tickets:validation.multi.removeConfirmTitle')}
        message={toRemove ? t('tickets:validation.multi.removeConfirmBody', { label: tabLabel(toRemove) }) : ''}
        confirmLabel={t('tickets:validation.multi.removeConfirmBtn')}
        cancelLabel={t('tickets:validation.multi.cancel')}
        busy={removing}
        onCancel={() => setToRemove(null)}
        onConfirm={() => { void remove(); }}
      />
    </GlassCard>
  );
}

export function TicketResolveCard({
  ticketId, resolvedAt, refreshKey, disabled, onSelect, onResolved,
}: {
  ticketId: string;
  resolvedAt: string | null | undefined;
  /** Change après chaque geste qui peut changer la préparation (verdict, ajout, retrait, envoi). */
  refreshKey: string;
  disabled: boolean;
  onSelect: (jvid: number) => void;
  onResolved: (res: { data: AddressValidationPanelsResponse; uniticketSync?: UniticketWriteResult }) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [preview, setPreview] = useState<TicketResolvePreview | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [recap, setRecap] = useState('');
  const [edited, setEdited] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setState('loading');
    fetchResolvePreview(ticketId)
      .then((res) => {
        if (!alive) return;
        setPreview(res.data);
        setState('ready');
        // Un texte retouché par le validateur n'est pas écrasé par une nouvelle préparation.
        setRecap((prev) => (edited ? prev : res.data.recap));
      })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, refreshKey]);

  async function resolve() {
    setBusy(true);
    try {
      const res = await resolveTicket(ticketId, recap);
      setConfirming(false);
      setEdited(false);
      onResolved(res);
    } catch (err) {
      setConfirming(false);
      toast.error(ticketAddressErrorText(err, t), { duration: 10000 });
    } finally {
      setBusy(false);
    }
  }

  const blockerText = (b: TicketResolvePreview['blockers'][number]) => {
    const label = String(b.params?.label ?? `JVID ${b.params?.jvid ?? '—'}`);
    switch (b.code) {
      case 'ADDRESS_PENDING': return t('tickets:validation.multi.resolve.blockerPending', { label });
      case 'ADDRESS_ELSEWHERE': return t('tickets:validation.multi.resolve.blockerElsewhere', { label });
      case 'HANDOFF_PENDING': return t('tickets:validation.multi.resolve.blockerHandoff');
      case 'NOT_MULTI_ADDRESS': return t('tickets:validation.multi.resolve.blockerNotMulti');
      case 'NO_DMP_OPERATOR': return t('tickets:validation.multi.resolve.blockerNoOperator');
      default: return b.code;
    }
  };

  return (
    <div id="av-resolve" className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-bold text-slate-800">{t('tickets:validation.multi.resolve.title')}</h3>
      </div>
      <p className="mb-3 text-xs text-slate-500">{t('tickets:validation.multi.resolve.sub')}</p>
      {resolvedAt && (
        <p role="status" className="mb-3 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-800">
          {t('tickets:validation.multi.resolve.resolvedNote', { date: new Date(resolvedAt).toLocaleString() })}
        </p>
      )}
      {state === 'loading' && (
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('tickets:validation.multi.resolve.loading')}
        </p>
      )}
      {state === 'error' && <p className="text-xs text-red-700">{t('tickets:validation.multi.resolve.error')}</p>}
      {state === 'ready' && preview && !resolvedAt && (
        <>
          {preview.blockers.length > 0 && (
            <ul className="mb-3 space-y-1">
              {preview.blockers.map((b, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 text-xs text-amber-800">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                  {blockerText(b)}
                  {typeof b.params?.jvid === 'number' && (
                    <button type="button" onClick={() => onSelect(b.params!.jvid as number)}
                      className="font-semibold text-brand-rose underline underline-offset-2">
                      {t('tickets:validation.multi.resolve.openTab')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <label htmlFor="av-resolve-recap" className="text-xs font-semibold text-slate-600">
            {t('tickets:validation.multi.resolve.recapLabel')}
          </label>
          <textarea
            id="av-resolve-recap"
            value={recap}
            onChange={(e) => { setRecap(e.target.value); setEdited(true); }}
            rows={Math.min(14, recap.split('\n').length + 2)}
            maxLength={10000}
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-rose/40"
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={disabled || !preview.ready || !recap.trim() || busy}
              className="bg-brand-gradient text-white hover:opacity-90"
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
              {t('tickets:validation.multi.resolve.btn')}
            </Button>
            {edited && (
              <button type="button" onClick={() => { setRecap(preview.recap); setEdited(false); }}
                className="text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900">
                {t('tickets:validation.multi.resolve.regen')}
              </button>
            )}
          </div>
        </>
      )}
      <ConfirmDialog
        open={confirming}
        tone="brand"
        icon={<Send className="h-5 w-5" />}
        title={t('tickets:validation.multi.resolve.confirmTitle')}
        message={t('tickets:validation.multi.resolve.confirmBody')}
        confirmLabel={t('tickets:validation.multi.resolve.confirmBtn')}
        cancelLabel={t('tickets:validation.multi.cancel')}
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => { void resolve(); }}
      />
    </div>
  );
}
